import { COLORS } from '@/app/constants';
import { useFetcher } from '@/app/hooks/fetcher';
import { useExchangeStore } from '@/app/hooks/trade/exchangeStore';
import EmptyPosition from '@/app/components/EmptyPosition';
import { StyledDatePicker } from '@/styles/Qstyle';
import { View } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import EntryTime from '@app/components/EntryTime';
import { Pagination, Select, Space, Table } from 'antd';
import koKR from 'antd/locale/ko_KR';
import enUS from 'antd/locale/en_US';
import BigNumber from 'bignumber.js';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionHistorySection } from './styles';

// 헤더 스타일 (TradePositionsTable과 동일)
const headerStyle = {
  fontFamily: 'NanumSquareRound',
  fontSize: 12,
  color: '#b3b5bd',
  letterSpacing: '-0.24px',
  whiteSpace: 'nowrap' as const,
};

// 셀 데이터 공통 스타일
const cellStyle = {
  display: 'flex',
  letterSpacing: 0,
  whiteSpace: 'nowrap' as const,
  height: 17,
  alignItems: 'flex-end' as const,
  color: '#fff',
  fontFamily: 'NanumSquareRound',
  fontSize: 12,
  lineHeight: 1,
};

// 숫자 강조 스타일
const cellStyleBold = {
  fontWeight: 700,
  fontFamily: 'NanumSquareRound',
  letterSpacing: 0,
  whiteSpace: 'nowrap' as const,
  color: '#fff',
  lineHeight: 'normal',
  fontSize: 13,
};

interface TransactionHistoryProps {
  parentHeight?: number;
}

const TransactionHistory = ({ parentHeight }: TransactionHistoryProps) => {
  const { theme } = useContext(ThemeContext);
  const { t, i18n } = useTranslation();

  // DatePicker locale 설정
  const datePickerLocale = i18n.language === 'ko' ? koKR.DatePicker : enUS.DatePicker;

  const [startDate, setStartDate] = useState<string | null>('');
  const [endDate, setEndDate] = useState<string | null>('');
  const [type, setType] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const { EPs } = useExchangeStore();
  const headerRef = useRef<any>(null);

  const KEYURL = `/trade/transaction?${startDate && endDate ? `sdate=${startDate}&edate=${endDate}` : ''}${type ? `&type=${type}` : ''}${page ? `&page=${page}` : '&page=1'}`;
  const { data } = useFetcher(KEYURL, true);
  const list = data?.result?.result?.list || [];
  const total = data?.result?.result?.totalCount || 0;
  const redColor = COLORS.lightRed;
  const blueColor = COLORS.lightGreen;

  const handleTypeChange = (newType: string) => {
    setType(newType);
    setPage(1);
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date ? date.format('YYYY-MM-DD') : null);
    if (endDate) {
      setPage(1);
    }
  };

  const handleEndDateChange = (date: any) => {
    setEndDate(date ? date.format('YYYY-MM-DD') : null);
    if (startDate) {
      setPage(1);
    }
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setType('');
    setPage(1);
  };

  const columns = [
    {
      title: <span style={headerStyle}>{t('orderTime')}</span>,
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'left' as const,
      width: '20%',
      onCell: () => ({
        style: {
          borderBottom: `1px solid ${theme.COLORS.transactionTableBorder}`,
          padding: '12px 8px',
        },
      }),
      render: (text: string) => {
        return (
          <EntryTime
            time={text || new Date().toISOString()}
            format={'YYYY-MM-DD HH:mm:ss'}
            style={{ ...cellStyle, justifyContent: 'flex-start' }}
          />
        );
      },
    },
    {
      title: <span style={headerStyle}>{t('transactionType')}</span>,
      dataIndex: 'type',
      key: 'type',
      align: 'left' as const,
      width: '20%',
      onCell: () => ({
        style: {
          borderBottom: `1px solid ${theme.COLORS.transactionTableBorder}`,
          padding: '12px 8px',
        },
      }),
      render: (text: string) => {
        const getTypeName = () => {
          switch (text) {
            case 'fee':
              return t('commission');
            case 'funding_fee':
              return t('fundingFee');
            case 'pnl':
              return t('realizedPnl');
            case 'liquid':
              return t('liquidationClearance');
            case 'referral':
              return t('referral');
            case 'event':
              return t('event');
            default:
              return text;
          }
        };
        return <div style={{ ...cellStyle }}>{getTypeName()}</div>;
      },
    },
    {
      title: <span style={headerStyle}>{t('amount')} (USDT)</span>,
      dataIndex: 'amount',
      key: 'amount',
      align: 'left' as const,
      width: '20%',
      onCell: () => ({
        style: {
          borderBottom: `1px solid ${theme.COLORS.transactionTableBorder}`,
          padding: '12px 8px',
        },
      }),
      render: (text: any, item: any) => {
        return (
          <div
            style={{
              ...cellStyleBold,
              color: text < 0 ? redColor : text > 0 ? blueColor : '#fff',
            }}
          >
            {text === 0
              ? 0
              : new BigNumber(item.amount).toFormat(4, BigNumber.ROUND_DOWN)}
          </div>
        );
      },
    },
    {
      title: <span style={headerStyle}>{t('assets')}</span>,
      dataIndex: 'fee_currency',
      key: 'fee_currency',
      align: 'left' as const,
      width: '20%',
      onCell: () => ({
        style: {
          borderBottom: `1px solid ${theme.COLORS.transactionTableBorder}`,
          padding: '12px 8px',
        },
      }),
      render: (text: string) => {
        return <div style={{ ...cellStyle }}>{text}</div>;
      },
    },
    {
      title: <span style={headerStyle}>{t('symbol')}</span>,
      dataIndex: 'symbol',
      key: 'symbol',
      align: 'left' as const,
      width: '20%',
      onCell: () => ({
        style: {
          borderBottom: `1px solid ${theme.COLORS.transactionTableBorder}`,
          padding: '12px 8px',
        },
      }),
      render: (text: string, item: any) => {
        const ep = EPs.find((ep: any) => ep.id === item.exchange_pairs_id);
        const coin = ep?.pairs?.split('/')[0];
        return (
          <div style={{ ...cellStyle, paddingRight: 24 }}>
            {coin}
            {item.fee_currency}
          </div>
        );
      },
    },
  ];

  return (
    <TransactionHistorySection
      parentHeight={parentHeight}
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: parentHeight ? 'hidden' : 'visible',
      }}
    >
      <View
        style={{
          color: theme.COLORS.textw,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 12px',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${theme.COLORS.PrimaryBorder}`,
          gap: 10,
        }}
        ref={headerRef}
      >
        <Select
          options={[
            { label: t('all'), value: '' },
            { label: t('commission'), value: 'fee' },
            // { label: t('fundingFee'), value: 'funding_fee' },
            { label: t('realizedPnl'), value: 'pnl' },
            { label: t('liquidationClearance'), value: 'liquid' },
            { label: t('referral'), value: 'referral' },
            { label: t('event'), value: 'event' },
          ]}
          style={{
            minWidth: 140,
            width: 'auto',
            border: `1px solid ${theme.COLORS.ButtonBorder}`,
          }}
          className="custom-select-no-radius"
          value={type}
          defaultValue={''}
          onChange={(e) => {
            handleTypeChange(e);
          }}
        />
        <Space.Compact
          style={{ padding: '10px 0', width: 'auto', gap: 10, borderRadius: 0 }}
          direction={'horizontal'}
        >
          <StyledDatePicker
            onChange={handleStartDateChange}
            style={{ border: parentHeight ? 'none' : '', borderRadius: 0 }}
            placeholder={t('selectDate')}
            locale={datePickerLocale}
          />
          <StyledDatePicker
            onChange={handleEndDateChange}
            style={{ border: parentHeight ? 'none' : '', borderRadius: 0 }}
            placeholder={t('selectDate')}
            locale={datePickerLocale}
          />
          <button
            style={{
              border: `1px solid ${theme.COLORS.ButtonBorder}`,
              borderRadius: 0,
              padding: '0 12px',
              color: theme.COLORS.textw,
            }}
            onClick={handleReset}
          >
            {t('all')}
          </button>
        </Space.Compact>
      </View>
      {list?.length ? (
        <Table
          columns={columns}
          style={{
            width: '100%',
            paddingLeft: '12px',
            margin: 0,
            boxSizing: 'border-box',
            backgroundColor: parentHeight ? 'transparent' : theme.COLORS.itemBg,
            paddingTop: 0,
          }}
          dataSource={list}
          pagination={false}
          size="small"
          scroll={
            // 부모높이 - 헤더높이(필터링) - 페이지네이션 높이 - 테이블 헤더 높이
            parentHeight &&
            list?.length * 20 >=
              parentHeight - headerRef.current?.clientHeight - 56 - 39
              ? {
                  x: true,
                  y: parentHeight - headerRef.current?.clientHeight - 56 - 39,
                }
              : { x: true }
          }
          className="transaction-history-table"
        />
      ) : (
        <EmptyPosition type="transactionHistory" />
      )}
      <Pagination
        total={total}
        pageSize={20}
        current={page}
        onChange={(e) => {
          setPage(e);
        }}
        align="center"
        showSizeChanger={false}
        style={{
          margin: `12px`,
        }}
      />
    </TransactionHistorySection>
  );
};

export default TransactionHistory;
